﻿import {isNullOrEmpty, StringNamedValue, translation} from "@2d-front-monorepo/core";
import {Button, Input, OptionList, OptionListEntry, Select, trim, upperCase} from "@2d-front-monorepo/design-library";
import {EnumLists, GroupedPermit, TariffId, Vehicles} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, useCallback, useContext, useMemo, useState} from "react";
import {getGlobalContext} from "../../hooks/getGlobalContext";
import {usePermits} from "../../hooks/usePermits";
import {useFilteredItems} from "./FilteredItemsProvider";
import {FilterManager, useFilter} from "./FilterManager";
import {FilterEntry, FilterPopoverContext, FiltersComponent} from "./FiltersComponent";

function StatusFilter({source}: { source: GroupedPermit[] }): JSX.Element {
	const popover = useContext(FilterPopoverContext);
	const [status, setStatus] = useState<string[]>([]);
	const filterFn = useCallback((items: GroupedPermit[]) => {
		return (status.length > 0 ? items.filter(item => status.includes(item.permit.status.type.toString(10))) : items);
	}, [status]);

	const entries = useMemo(() => {
		const result: OptionListEntry[] = [];
		const set = new Set(source.map(x => x.permit.status.type));

		for (const {label, value} of EnumLists.permitStatusType()) {
			if (!set.has(value)) continue;
			result.push({
				label: label,
				value: value.toString(10)
			});
		}

		return result;
	}, [source]);

	useFilter("status", filterFn);

	return (
		<div className="layout vertical padded inline">
			<OptionList name="status" values={status} items={entries} multiple={true} onChange={selected => {
				setStatus(selected.map(x => entries[x].value))
			}}/>

			<div className="form__buttons">
				<Button kind="primary" onClick={() => popover?.close()}>
					{translation(PermitTranslations.done)}
				</Button>
			</div>
		</div>);
}

function PlateFilter(): JSX.Element {
	const popover = useContext(FilterPopoverContext);
	const [plate, setPlate] = useState<string>("");
	const permitsState = usePermits();

	const filterFn = useCallback((items: GroupedPermit[]) => {
		const p = upperCase(trim(plate));
		return p
			? items.filter(item =>
				Vehicles.getValid(permitsState, item.permit.vehicles)
					.map(x => upperCase(x.entity.plate)).some(x => x.includes(p)))
			: items;
	}, [plate, permitsState]);

	useFilter("plate", filterFn);

	return (
		<div className="layout vertical padded inline">
			<Input label={translation(PermitTranslations.summary_page__filters__plate_label)}
			       name="plate" type="search"
			       value={plate}
			       onChange={e => setPlate(e.currentTarget.value ?? "")}/>

			<div className="form__buttons">
				<Button kind="primary" onClick={() => popover?.close()}>
					{translation(PermitTranslations.done)}
				</Button>
			</div>
		</div>);
}

function TariffFilter({source}: { source: GroupedPermit[] }): JSX.Element {
	const {installation} = getGlobalContext();
	const popover = useContext(FilterPopoverContext);
	const [tariff, setTariff] = useState<TariffId>("" as TariffId);
	const tariffs = useMemo(() => {
		const set = new Set(source.map(x => x.permit.tariffId));
		const result: StringNamedValue[] = [];
		for (const tariff of installation.tariffs) {
			if (!set.has(tariff.type)) continue;
			result.push({
				label: tariff.title,
				value: tariff.type
			});
		}

		return result;
	}, [source, installation.tariffs]);

	const filterFn = useCallback((items: GroupedPermit[]) =>
		isNullOrEmpty(tariff)
			? items
			: items.filter(item => item.permit.tariffId === tariff), [tariff]);

	useFilter("tariff", filterFn);

	return (
		<div className="layout vertical padded inline">
			<Select label={translation(PermitTranslations.permit_type)}
			        name="tariff"
			        items={tariffs}
			        placeholder={translation(PermitTranslations.summary_page__filters__tariff_placeholder)}
			        onChange={e => {
				        const selected = (e.currentTarget.selectedIndex ?? 0) - 1;
				        setTariff((selected === -1 ? undefined : tariffs[selected].value) as TariffId);
			        }}/>

			<div className="form__buttons">
				<Button kind="primary" onClick={() => popover?.close()}>
					{translation(PermitTranslations.done)}
				</Button>
			</div>
		</div>
	);
}

export function PermitsFilterComponent(): JSX.Element {
	const {source, setFiltered} = useFilteredItems<GroupedPermit>();

	return (
		<FilterManager source={source} onFiltered={setFiltered}>
			<FiltersComponent title={translation(PermitTranslations.summary_page__filters)}>
				<FilterEntry title={translation(PermitTranslations.summary_page__filters__status_title)}
				             label={translation(PermitTranslations.summary_page__filters__status_label)}><StatusFilter source={source}/></FilterEntry>
				<FilterEntry title={translation(PermitTranslations.summary_page__filters__plate_title)}
				             label={translation(PermitTranslations.summary_page__filters__plate_label)}><PlateFilter/></FilterEntry>
				<FilterEntry title={translation(PermitTranslations.summary_page__filters__tariff_title)}
				             label={translation(PermitTranslations.summary_page__filters__tariff_label)}><TariffFilter source={source}/></FilterEntry>
			</FiltersComponent>
		</FilterManager>
	)
}
